import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Calendar, ArrowRight, Clock, MapPin, CreditCard, Loader } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useNotification } from '../../../context/NotificationContext';

interface Booking {
  id: string;
  reservation_id: string;
  booking_date: string;
  address: string;
  services: {
    name: string;
    price: number;
    duration: number;
  };
  discount_amount: number;
}

export default function BookingConfirmation() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestBooking = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }

        const { data, error } = await supabase
          .from('bookings')
          .select(`
            id,
            reservation_id,
            booking_date,
            address,
            discount_amount,
            services (
              name,
              price,
              duration
            )
          `)
          .eq('user_id', session.user.id)
          .eq('status', 'confirmed')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (error) throw error;
        setBooking(data);
      } catch (error) {
        console.error('Error fetching booking:', error);
        showNotification('error', 'Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };

    fetchLatestBooking();
  }, [navigate, showNotification]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="rounded-full bg-red-100 p-3 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
            <Calendar className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            No Booking Found
          </h1>
          <p className="text-gray-600 mb-8">
            We couldn't find your booking. Please try booking a service again.
          </p>
          <Link
            to="/book"
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg text-white bg-blue-600 hover:bg-blue-700 font-medium transition-colors"
          >
            Book a Service
          </Link>
        </div>
      </div>
    );
  }

  const finalPrice = booking.services.price - booking.discount_amount;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="rounded-full bg-green-100 p-3 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Booking Confirmed!
            </h1>
            
            <p className="text-gray-600">
              Thank you for choosing Aussi Clean. We'll send you a confirmation email with all the details shortly.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium w-fit text-green-600 bg-green-100">
                        <CheckCircle className="w-5 h-5" />
                        Confirmed
                      </span>
                      <span className="text-sm text-gray-500 font-mono mt-1">
                        {booking.reservation_id}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {booking.services.name}
                    </h3>
                  </div>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  ${(finalPrice / 100).toFixed(2)}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-5 h-5 flex-shrink-0" />
                  <span>
                    {new Date(booking.booking_date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-5 h-5 flex-shrink-0" />
                  <span>
                    {new Date(booking.booking_date).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: 'numeric',
                    })}
                    <span className="text-gray-400 mx-2">·</span>
                    {booking.services.duration} minutes
                  </span>
                </div>
                <div className="md:col-span-2 flex items-start gap-2 text-gray-600">
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-1" />
                  <span>{booking.address}</span>
                </div>
              </div>

              {booking.discount_amount > 0 && (
                <div className="border-t pt-4 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Service Price:</span>
                    <span>${(booking.services.price / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-green-600 mt-1">
                    <span>Discount:</span>
                    <span>-${(booking.discount_amount / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-gray-900 mt-2 pt-2 border-t">
                    <span>Total:</span>
                    <span>${(finalPrice / 100).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 mt-8">
            <h2 className="font-semibold text-lg mb-4">What's Next?</h2>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                  <span className="text-blue-600 text-sm font-medium">1</span>
                </div>
                <p className="text-gray-600">You'll receive a confirmation email with your booking details</p>
              </li>
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                  <span className="text-blue-600 text-sm font-medium">2</span>
                </div>
                <p className="text-gray-600">Our team will review your booking and prepare for the service</p>
              </li>
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                  <span className="text-blue-600 text-sm font-medium">3</span>
                </div>
                <p className="text-gray-600">We'll send a reminder 24 hours before your scheduled service</p>
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              to="/book"
              className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent rounded-lg text-white bg-blue-600 hover:bg-blue-700 font-medium transition-colors"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book Another Service
            </Link>
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors"
            >
              View Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}